import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getClientById, updateClient, getClientPricingCategories } from '../api';
import useCurrentUser from '../hooks/useCurrentUser';
import useClientQuotationCategories from '../hooks/useClientQuotationCategories';
import { hasPermission } from '../utils/permissions';
import InlineLoading from '../components/InlineLoading';
import ClientQuotationCategoriesPanel from '../components/ClientQuotationCategoriesPanel';
import ClientPricingDiscountConfigurator from '../components/ClientPricingDiscountConfigurator';
import { notify } from '../utils/alertBus';

const EditClientPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState({
        name: '',
        email: '',
        giro: '',
        rut: '',
        address: '',
        city: '',
        phone: '',
        creditLimit: '',
        creditBlocked: false,
        creditWarningNote: '',
        clientPricingCategoryId: '',
    });
    const [pricingCategories, setPricingCategories] = useState([]);
    const [pricingCategoriesLoading, setPricingCategoriesLoading] = useState(false);
    const [clientPricingMode, setClientPricingMode] = useState('none');
    const [customPricingDiscounts, setCustomPricingDiscounts] = useState([]);
    const [saveCustomPricingAsCategory, setSaveCustomPricingAsCategory] = useState(false);
    const [customPricingCategoryName, setCustomPricingCategoryName] = useState('');
    const [customPricingCategoryDescription, setCustomPricingCategoryDescription] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { user } = useCurrentUser();
    const canUpdateClient = hasPermission(user, 'client.update');
    const canManageCredit = hasPermission(user, 'branch.access.any');
    const canManageClientPricing = hasPermission(user, 'client_pricing.manage');
    const quotationCategories = useClientQuotationCategories(id, Boolean(client.account_available));
    const purchaseOrderCategories = useClientQuotationCategories(id, Boolean(client.account_available), 'purchaseOrder');

    useEffect(() => {
        const fetchClient = async () => {
            try {
                const data = await getClientById(id);
                setClient({
                    ...data,
                    clientPricingCategoryId: data?.clientPricingCategoryId || '',
                });
                setClientPricingMode(data?.clientPricingMode || (data?.clientPricingCategoryId ? 'category' : 'none'));
                setCustomPricingCategoryName(data?.name ? `${data.name} descuentos` : '');
            } catch (err) {
                setError('Error al cargar los datos del cliente.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchClient();
    }, [id]);

    useEffect(() => {
        if (!canManageClientPricing) return;
        let mounted = true;
        const loadPricingCategories = async () => {
            setPricingCategoriesLoading(true);
            try {
                const data = await getClientPricingCategories({ activeOnly: true });
                if (!mounted) return;
                const categories = Array.isArray(data?.categories) ? data.categories : [];
                setPricingCategories(categories);
            } catch (err) {
                if (mounted) setPricingCategories([]);
            } finally {
                if (mounted) setPricingCategoriesLoading(false);
            }
        };
        loadPricingCategories();
        return () => {
            mounted = false;
        };
    }, [canManageClientPricing]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setClient(prevClient => ({ ...prevClient, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canUpdateClient) return;
        try {
            const payload = { ...client };
            if (canManageCredit) {
                const trimmedLimit = String(client.creditLimit ?? '').trim();
                if (trimmedLimit === '') {
                    payload.creditLimit = null;
                } else {
                    const parsedLimit = Number(trimmedLimit);
                    if (!Number.isFinite(parsedLimit) || parsedLimit < 0) {
                        setError('El limite de credito debe ser un numero valido.');
                        return;
                    }
                    payload.creditLimit = parsedLimit;
                }
                payload.creditBlocked = Boolean(client.creditBlocked);
                payload.creditWarningNote = client.creditWarningNote?.trim() || null;
            } else {
                delete payload.creditLimit;
                delete payload.creditBlocked;
                delete payload.creditWarningNote;
            }
            if (canManageClientPricing) {
                if (clientPricingMode === 'category' && !client.clientPricingCategoryId) {
                    setError('Selecciona una categoria de descuento.');
                    return;
                }
                if (clientPricingMode === 'custom' && saveCustomPricingAsCategory && !customPricingCategoryName.trim()) {
                    setError('Ingresa un nombre para crear la categoria de descuento.');
                    return;
                }
                payload.clientPricingMode = clientPricingMode;
                payload.clientPricingCategoryId = clientPricingMode === 'category'
                    ? client.clientPricingCategoryId || null
                    : null;
                payload.customPricingDiscounts = clientPricingMode === 'custom' ? customPricingDiscounts : [];
                payload.saveCustomPricingAsCategory = clientPricingMode === 'custom' && saveCustomPricingAsCategory;
                payload.customPricingCategoryName = customPricingCategoryName.trim();
                payload.customPricingCategoryDescription = customPricingCategoryDescription.trim();
            } else {
                delete payload.clientPricingCategoryId;
            }
            await updateClient(id, payload);
            if (client.account_available && quotationCategories.loaded) {
                await quotationCategories.saveCategoryIds();
            }
            if (client.account_available && purchaseOrderCategories.loaded) {
                await purchaseOrderCategories.saveCategoryIds();
            }
            notify('Cliente actualizado exitosamente.');
            navigate('/client');
        } catch (err) {
            setError('Error al actualizar el cliente.');
            console.error(err);
        }
    };

    if (loading) {
        return (
            <p className="text-center mt-6">
                <InlineLoading />
            </p>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 pt-24 px-4 pb-10">
            <div className="mx-auto max-w-4xl overflow-hidden rounded-lg bg-white shadow">
                <div className="border-b border-gray-100 bg-gray-50 px-6 py-4">
                    <p className="text-xs font-semibold uppercase text-gray-500">Clientes</p>
                    <h1 className="text-2xl font-bold text-gray-900">Editar cliente</h1>
                    <p className="mt-1 text-sm text-gray-600">
                        Actualiza datos comerciales, descuentos y acceso del portal.
                    </p>
                </div>
                {error && <p className="mx-6 mt-4 rounded border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
                <form onSubmit={handleSubmit} className="space-y-6 p-6">
                    <section className="space-y-4">
                        <div>
                            <h2 className="text-sm font-semibold text-gray-800">Datos comerciales</h2>
                            <p className="text-xs text-gray-500">Informacion usada en ventas, cotizaciones y documentos.</p>
                        </div>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="md:col-span-2">
                                <label className="text-xs font-semibold uppercase text-gray-600">Nombre</label>
                                <input type="text" name="name" value={client.name} onChange={handleChange} placeholder="Nombre" className="mt-2 w-full rounded-lg border border-gray-300 p-3" required disabled={!canUpdateClient} />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase text-gray-600">Email</label>
                                <input type="email" name="email" value={client.email} onChange={handleChange} placeholder="Email" className="mt-2 w-full rounded-lg border border-gray-300 p-3" disabled={!canUpdateClient} />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase text-gray-600">RUT</label>
                                <input type="text" name="rut" value={client.rut} onChange={handleChange} placeholder="RUT" className="mt-2 w-full rounded-lg border border-gray-300 p-3" required disabled={!canUpdateClient} />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase text-gray-600">Giro</label>
                                <input type="text" name="giro" value={client.giro || ''} onChange={handleChange} placeholder="Giro" className="mt-2 w-full rounded-lg border border-gray-300 p-3" disabled={!canUpdateClient} />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase text-gray-600">Telefono</label>
                                <input type="text" name="phone" value={client.phone} onChange={handleChange} placeholder="Telefono" className="mt-2 w-full rounded-lg border border-gray-300 p-3" disabled={!canUpdateClient} />
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-xs font-semibold uppercase text-gray-600">Direccion</label>
                                <input type="text" name="address" value={client.address} onChange={handleChange} placeholder="Direccion" className="mt-2 w-full rounded-lg border border-gray-300 p-3" disabled={!canUpdateClient} />
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-xs font-semibold uppercase text-gray-600">Ciudad</label>
                                <input type="text" name="city" value={client.city} onChange={handleChange} placeholder="Ciudad" className="mt-2 w-full rounded-lg border border-gray-300 p-3" disabled={!canUpdateClient} />
                            </div>
                        </div>
                    </section>
                    {canManageClientPricing && (
                        <ClientPricingDiscountConfigurator
                            mode={clientPricingMode}
                            onModeChange={setClientPricingMode}
                            categoryValue={client.clientPricingCategoryId || ''}
                            onCategoryChange={handleChange}
                            categories={pricingCategories}
                            categoriesLoading={pricingCategoriesLoading}
                            customDiscounts={customPricingDiscounts}
                            onCustomDiscountsChange={setCustomPricingDiscounts}
                            saveAsCategory={saveCustomPricingAsCategory}
                            onSaveAsCategoryChange={setSaveCustomPricingAsCategory}
                            categoryName={customPricingCategoryName}
                            onCategoryNameChange={setCustomPricingCategoryName}
                            categoryDescription={customPricingCategoryDescription}
                            onCategoryDescriptionChange={setCustomPricingCategoryDescription}
                            clientId={id}
                            disabled={!canUpdateClient}
                        />
                    )}
                    {canManageCredit && (
                        <section className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                            <div>
                                <h2 className="text-sm font-semibold text-gray-800">Credito</h2>
                                <p className="text-xs text-gray-500">Controla limite y bloqueos comerciales del cliente.</p>
                            </div>
                            <input
                                type="number"
                                min="0"
                                name="creditLimit"
                                value={client.creditLimit ?? ''}
                                onChange={handleChange}
                                placeholder="Limite de credito (opcional)"
                                className="w-full rounded-lg border border-gray-300 bg-white p-3"
                                disabled={!canUpdateClient}
                            />
                            <label className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    name="creditBlocked"
                                    checked={Boolean(client.creditBlocked)}
                                    onChange={handleChange}
                                    disabled={!canUpdateClient}
                                />
                                Bloquear credito para este cliente
                            </label>
                            <textarea
                                name="creditWarningNote"
                                value={client.creditWarningNote || ''}
                                onChange={handleChange}
                                placeholder="Nota de advertencia"
                                className="w-full rounded-lg border border-gray-300 bg-white p-3"
                                rows={3}
                                disabled={!canUpdateClient}
                            />
                        </section>
                    )}
                    {client.account_available && (
                        <>
                            <ClientQuotationCategoriesPanel
                                categoryOptions={quotationCategories.categoryOptions}
                                selectedCategoryIds={quotationCategories.selectedCategoryIds}
                                loading={quotationCategories.loading}
                                saving={quotationCategories.saving}
                                error={quotationCategories.error}
                                disabled={!canUpdateClient}
                                onToggle={quotationCategories.toggleCategory}
                            />
                            <ClientQuotationCategoriesPanel
                                categoryOptions={purchaseOrderCategories.categoryOptions}
                                selectedCategoryIds={purchaseOrderCategories.selectedCategoryIds}
                                loading={purchaseOrderCategories.loading}
                                saving={purchaseOrderCategories.saving}
                                error={purchaseOrderCategories.error}
                                disabled={!canUpdateClient}
                                title="Categorias para ordenes"
                                description="Controla que categorias puede ver este cliente al crear ordenes de compra."
                                emptyWarning="Sin categorias seleccionadas: este cliente vera vacio el catalogo de ordenes de compra."
                                onToggle={purchaseOrderCategories.toggleCategory}
                            />
                        </>
                    )}
                    <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-end">
                        {canUpdateClient && (
                          <button type="submit" className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">Guardar</button>
                        )}
                        <button type="button" onClick={() => navigate('/client')} className="rounded-lg border border-gray-200 px-4 py-2 text-gray-700 hover:text-gray-900">Cancelar</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditClientPage;
